// Traducción de los rechazos del paso 06 (cotización) a un mensaje que el vendedor puede leer en
// voz alta frente al cliente, más la ACCIÓN que de verdad los resuelve.
//
// El `msg` del motor es prosa interna («piso acumulado de las políticas especiales en alcance») y
// nunca se muestra crudo en un código conocido.
import { RequestTimeoutError } from '@/api/client';
import { ApiError } from '@/api/errors';
import { formatMXN } from '@/shared/lib/format/money';
import { INTEGRATION_ACTION_LABEL, integrationFailure } from './integrationErrors';

/**
 * Qué puede HACER el vendedor desde la pantalla.
 *
 * `retry` solo donde reintentar tiene sentido (502, timeout, red). Un rechazo de negocio nunca lo
 * lleva: el botón prometería un desenlace imposible. `payment-method` regresa al paso 04, que es
 * donde viven las otras financieras y el contado. `build-sale` regresa a «Accesorios».
 */
export type QuoteRecovery = 'retry' | 'product' | 'build-sale' | 'payment-method' | 'none';

export interface QuoteFailure {
    message: string;
    /** La acción principal: es la que se pinta como botón. */
    recovery: QuoteRecovery;
    /**
     * Segunda salida válida, en enlace discreto bajo la principal. Solo donde el rechazo tiene DOS
     * caminos reales y hacerlos botones gemelos obligaría al vendedor a elegir a ciegas.
     */
    secondary?: 'payment-method';
    /**
     * El vendedor lo corrige moviendo un control de ESTA pantalla (enganche o plazo). Cuando es
     * true la pantalla conserva los controles y el mensaje va junto a ellos.
     */
    adjustableHere: boolean;
    /** Plazos que el plan sí acepta, cuando el silo los mandó en `details.available` (semanas). */
    availableTermWeeks?: number[];
    /** Etiqueta del botón de la acción principal cuando no es la de siempre. */
    actionLabel?: string;
}

/** El fallo antes de clasificarlo: `quoteFailure` le estampa `adjustableHere`. */
type BaseQuoteFailure = Omit<QuoteFailure, 'adjustableHere'>;

const ASK_MANAGER = 'Avisa a tu gerente: no se puede cotizar hasta que lo corrijan.';

/**
 * Cierre de los rechazos donde el crédito de SureKeep no procede pero la venta sí. No nombra a las
 * financieras externas: el paso 04 puede no tener ninguna elegible, y el contado siempre está.
 */
const OFFER_ALTERNATIVES = 'Revisa con el cliente las otras formas de pago.';

/**
 * Rechazos que el vendedor resuelve moviendo el enganche o el plazo sin salir del paso 06.
 * Lo que no está aquí no tiene control que mover en esta pantalla.
 */
const ADJUSTABLE_HERE: ReadonlySet<string> = new Set([
    // Subir el enganche baja el capital a financiar bajo el tope del plan.
    'AMOUNT_EXCEEDS_LIMIT',
    'DOWN_PAYMENT_TOO_LOW',
    'DOWN_PAYMENT_TOO_HIGH',
    'DOWN_PAYMENT_OUT_OF_RANGE',
    'TERM_NOT_AVAILABLE',
    // Su mensaje pide cambiar el enganche o el plazo: los dos controles están en la pantalla.
    'INVALID_PLAN_PARAMETERS',
]);

/**
 * Rechazos con mensaje fijo. Los códigos salen de los `DomainError` de los tres lambdas de la
 * cadena: `retail-wizard-business` (PLAN_USABILITY_CODES + STORE_POLICY_REJECTION_CODES),
 * `loan-credit-business` y `loan-offerings-business`. Nada de códigos inventados.
 */
const FAILURE_BY_CODE: Record<string, BaseQuoteFailure> = {
    // El capital a financiar supera el tope del plan del nivel. Bajar el monto es la salida más
    // probable, y si el cliente no quiere otro equipo todavía le queda vender por otra vía.
    AMOUNT_EXCEEDS_LIMIT: {
        message: `El plan del nivel de este cliente no alcanza para financiar este equipo. Sube el enganche o elige un equipo más barato. ${OFFER_ALTERNATIVES}`,
        recovery: 'product',
        secondary: 'payment-method',
    },
    DOWN_PAYMENT_TOO_LOW: {
        message:
            'El enganche es menor al mínimo que pide el plan del cliente. Súbelo para continuar.',
        recovery: 'none',
    },
    DOWN_PAYMENT_TOO_HIGH: {
        message:
            'El enganche supera el máximo que permite el plan del cliente. Bájalo para continuar.',
        recovery: 'none',
    },
    PLAN_INACTIVE: {
        message: `El plan de crédito del nivel de este cliente está inactivo. ${ASK_MANAGER}`,
        recovery: 'none',
    },
    CREDIT_PLAN_NOT_FOUND: {
        message: `El plan de crédito de este cliente ya no está en el catálogo. ${ASK_MANAGER}`,
        recovery: 'none',
    },
    PLAN_NOT_AVAILABLE_FOR_LEVEL: {
        message: `No hay un plan de crédito activo para el nivel de este cliente. ${ASK_MANAGER}`,
        recovery: 'none',
    },
    // El crédito de SureKeep no procede para este cliente, pero la venta no se cae con él.
    SCORE_ADVERSE: {
        message: `No podemos darle crédito a este cliente. ${OFFER_ALTERNATIVES}`,
        recovery: 'payment-method',
    },
    // ── Políticas especiales de la tienda. El motor las manda con su prosa interna («piso
    // acumulado», «palanca», «compuerta»): acá se reescriben en lo que el vendedor puede decir.
    SCI_BELOW_STORE_POLICY_FLOOR: {
        message: `Este cliente no alcanza el puntaje que se pide en esta tienda para dar crédito. ${OFFER_ALTERNATIVES}`,
        recovery: 'payment-method',
    },
    STORE_POLICY_SUSPENDED: {
        message: `Ahora mismo esta tienda no está dando crédito. ${OFFER_ALTERNATIVES}`,
        recovery: 'payment-method',
    },
    // El veto es de ESTE equipo, así que otro del catálogo es la primera salida a probar.
    SKU_NOT_ALLOWED: {
        message: `Este equipo no se financia en esta tienda. Elige otro equipo. ${OFFER_ALTERNATIVES}`,
        recovery: 'product',
        secondary: 'payment-method',
    },
    // Las reglas de la tienda y las del plan se contradicen: no hay enganche ni plazo que salve
    // esta cotización, y el gerente tiene que enterarse para que no se repita en la siguiente venta.
    STORE_POLICY_MAKES_PLAN_UNQUOTABLE: {
        message: `Las reglas de crédito de esta tienda no dejan ninguna cotización posible para este cliente. Avisa a tu gerente. ${OFFER_ALTERNATIVES}`,
        recovery: 'payment-method',
    },
    // Fail-closed del motor: le falta un dato para decidir. Es configuración, no el cliente.
    STORE_POLICY_SCOPE_UNKNOWN: {
        message: `Falta un dato de esta tienda para poder revisar si el crédito procede. ${ASK_MANAGER}`,
        recovery: 'none',
    },
    // El único de la lista que SÍ es una falla técnica: el wizard no pudo hablar con el motor.
    EXTERNAL_INTEGRATION_ERROR: {
        message:
            'No se pudo hablar con el motor de crédito. Espera unos segundos e intenta de nuevo.',
        recovery: 'retry',
    },
    // El código viajó a la ancla nueva; la pieza de Promociones en «Accesorios» lo revalida.
    PROMOTION_NOT_REVALIDATED: {
        message:
            'El código de descuento todavía no se valida para el equipo nuevo. Regresa a «Accesorios» para validarlo y luego vuelve a cotizar.',
        recovery: 'build-sale',
    },
    PRODUCT_NOT_FINANCEABLE: {
        message:
            'Este equipo no se puede financiar con SureKeep. Cóbralo de contado desde las formas de pago.',
        recovery: 'payment-method',
    },
    INVALID_SESSION_STATE: {
        message: 'Esta venta ya no está activa. Vuelve al portal e inicia una nueva.',
        recovery: 'none',
    },
    SESSION_NOT_FOUND: {
        message: 'Esta venta ya no existe. Vuelve al portal e inicia una nueva.',
        recovery: 'none',
    },
    PERMISSION_DENIED: {
        message: 'No tienes permiso para cotizar esta venta.',
        recovery: 'none',
    },
};

/** Lee un número de los `details` del silo; `undefined` si no viajó o no es número. */
function detail(error: ApiError, key: string): number | undefined {
    const data = error.data as Record<string, unknown> | null | undefined;
    const value = data?.[key];
    return typeof value === 'number' ? value : undefined;
}

/**
 * Convierte a pesos un porcentaje del silo (decimal sobre 1: `0.4` = 40 %) contra el precio en
 * cents. `undefined` cuando falta cualquiera de los dos: sin precio no hay cifra que teclear.
 */
function pctToPesos(
    pct: number | undefined,
    priceCents: number | undefined,
    round: 'up' | 'down',
): number | undefined {
    if (pct === undefined || priceCents === undefined || priceCents <= 0) return undefined;
    const pesos = (priceCents * pct) / 100;
    return round === 'up' ? Math.ceil(pesos) : Math.floor(pesos);
}

/** Plazos que el plan sí acepta, de menor a mayor. `termWeeksOptions` es la clave vigente. */
function termOptions(error: ApiError): number[] {
    const data = error.data as
        | { termWeeksOptions?: unknown; available?: unknown }
        | null
        | undefined;
    const raw = Array.isArray(data?.termWeeksOptions)
        ? data.termWeeksOptions
        : Array.isArray(data?.available)
          ? data.available
          : [];
    return raw.filter((w): w is number => typeof w === 'number').sort((a, b) => a - b);
}

/**
 * Enganche fuera de la cota del plan, con la cifra EN PESOS que el vendedor tiene que teclear.
 * Sin el pct del silo o sin precio se queda con el mensaje genérico: nada de huecos.
 */
function downPaymentBoundFailure(
    error: ApiError,
    bound: 'min' | 'max',
    priceCents: number | undefined,
): BaseQuoteFailure {
    const fallback =
        FAILURE_BY_CODE[bound === 'min' ? 'DOWN_PAYMENT_TOO_LOW' : 'DOWN_PAYMENT_TOO_HIGH'];
    const pesos =
        bound === 'min'
            ? pctToPesos(detail(error, 'minDownPaymentPct'), priceCents, 'up')
            : pctToPesos(detail(error, 'maxDownPaymentPct'), priceCents, 'down');
    if (pesos === undefined) return fallback;
    return {
        ...fallback,
        message:
            bound === 'min'
                ? `El enganche debe ser de al menos ${formatMXN(pesos)}. Súbelo para continuar.`
                : `El enganche no puede pasar de ${formatMXN(pesos)}. Bájalo para continuar.`,
    };
}

/** El tope del plan con las dos cifras en pesos: hasta cuánto financia y cuánto pide esta venta. */
function amountExceedsLimitFailure(error: ApiError): BaseQuoteFailure {
    const fallback = FAILURE_BY_CODE.AMOUNT_EXCEEDS_LIMIT;
    const maxCents = detail(error, 'maxFinancedAmountCents');
    if (maxCents === undefined) return fallback;
    const financedCents = detail(error, 'financedAmountCents');
    const needs =
        financedCents !== undefined
            ? ` y esta venta necesita financiar ${formatMXN(financedCents / 100)}`
            : '';
    // Solo el hecho. La instrucción la da el control: la barra ya arranca en el enganche mínimo
    // financiable, así que decir «súbelo a X» sería repetir el número que el vendedor ya tiene.
    return {
        ...fallback,
        message: `El plan del nivel de este cliente financia hasta ${formatMXN(maxCents / 100)}${needs}.`,
    };
}

/** El puntaje del cliente contra el que pide la tienda, sin la jerga de «piso» ni «SCI». */
function sciBelowFloorFailure(error: ApiError): BaseQuoteFailure {
    const fallback = FAILURE_BY_CODE.SCI_BELOW_STORE_POLICY_FLOOR;
    const score = detail(error, 'score');
    const floor = detail(error, 'floor');
    if (score === undefined || floor === undefined) return fallback;
    return {
        ...fallback,
        message: `Este cliente tiene ${String(score)} puntos y en esta tienda se piden ${String(floor)} para dar crédito. ${OFFER_ALTERNATIVES}`,
    };
}

/** Rango del enganche en pesos con los cents que manda el silo en `details`. */
function downPaymentRangeFailure(error: ApiError): BaseQuoteFailure {
    const minCents = detail(error, 'minCents');
    const maxCents = detail(error, 'maxCents');
    if (minCents !== undefined && maxCents !== undefined) {
        return {
            message: `El enganche debe estar entre ${formatMXN(minCents / 100)} y ${formatMXN(maxCents / 100)}.`,
            recovery: 'none',
        };
    }
    if (minCents !== undefined) {
        return {
            message: `El enganche debe ser de al menos ${formatMXN(minCents / 100)}.`,
            recovery: 'none',
        };
    }
    return {
        message:
            'El enganche está fuera del rango que permite el plan del cliente. Ajústalo para continuar.',
        recovery: 'none',
    };
}

/** Plazos del catálogo del plan, con el que se pidió cuando el silo lo manda en `details`. */
function termFailure(error: ApiError): BaseQuoteFailure {
    const weeks = termOptions(error);
    if (weeks.length > 0) {
        const asked = detail(error, 'termWeeks');
        const head =
            asked !== undefined
                ? `El plan de este cliente no ofrece el plazo de ${String(asked)} semanas.`
                : 'El plan de este cliente no ofrece ese plazo.';
        return {
            message: `${head} Los disponibles son de ${weeks.join(', ')} semanas.`,
            recovery: 'none',
            availableTermWeeks: weeks,
        };
    }
    return {
        message: 'El plan de este cliente no ofrece ese plazo. Elige otro para continuar.',
        recovery: 'none',
    };
}

/**
 * `INVALID_PLAN_PARAMETERS` no trae `details`: su `msg` ES el parámetro que el simulador rechazó,
 * así que se conserva como detalle detrás de la instrucción al vendedor.
 */
function invalidParametersFailure(error: ApiError): BaseQuoteFailure {
    const detailText = error.msg !== '' && error.msg !== error.code ? ` (${error.msg})` : '';
    return {
        message: `Estos términos no son válidos para el plan del cliente. Cambia el enganche o el plazo.${detailText}`,
        recovery: 'none',
    };
}

/** Rechazos de la venta integrada a Zentral: cotizar de nuevo, cambiar la pieza o pasar a contado. */
function integrationQuoteFailure(error: ApiError): BaseQuoteFailure | undefined {
    const failure = integrationFailure(error);
    if (!failure) return undefined;
    if (failure.recovery === 'none') return { message: failure.message, recovery: 'none' };
    const recovery: QuoteRecovery =
        failure.recovery === 'quote'
            ? 'retry'
            : failure.recovery === 'cash'
              ? 'payment-method'
              : 'build-sale';
    return {
        message: failure.message,
        recovery,
        actionLabel: INTEGRATION_ACTION_LABEL[failure.recovery],
    };
}

/**
 * Mensaje + acción para un fallo de la cotización.
 *
 * ⚠️ El genérico NO afirma que falló un servicio externo: cuando el código es desconocido no
 * sabemos la causa, y prometer una causa técnica es lo que mandaba al vendedor a reintentar una
 * regla de negocio. Se muestra el `msg` del silo si lo hay —crudo pero VERDADERO— y solo se
 * ofrece reintentar cuando el status dice que fue transporte (5xx).
 */
function baseQuoteFailure(error: unknown, priceCents: number | undefined): BaseQuoteFailure {
    if (error instanceof RequestTimeoutError) {
        return {
            message: 'La cotización tardó demasiado. Revisa tu conexión e intenta de nuevo.',
            recovery: 'retry',
        };
    }
    if (!(error instanceof ApiError)) {
        return {
            message: 'No se pudo cotizar. Revisa tu conexión e intenta de nuevo.',
            recovery: 'retry',
        };
    }
    const integration = integrationQuoteFailure(error);
    if (integration) return integration;
    if (error.code === 'DOWN_PAYMENT_OUT_OF_RANGE') return downPaymentRangeFailure(error);
    if (error.code === 'DOWN_PAYMENT_TOO_LOW')
        return downPaymentBoundFailure(error, 'min', priceCents);
    if (error.code === 'DOWN_PAYMENT_TOO_HIGH')
        return downPaymentBoundFailure(error, 'max', priceCents);
    if (error.code === 'AMOUNT_EXCEEDS_LIMIT') return amountExceedsLimitFailure(error);
    if (error.code === 'SCI_BELOW_STORE_POLICY_FLOOR') return sciBelowFloorFailure(error);
    if (error.code === 'TERM_NOT_AVAILABLE') return termFailure(error);
    if (error.code === 'INVALID_PLAN_PARAMETERS') return invalidParametersFailure(error);

    const known = FAILURE_BY_CODE[error.code] as BaseQuoteFailure | undefined;
    if (known !== undefined) return known;

    const detailText = error.msg !== '' && error.msg !== error.code ? ` ${error.msg}` : '';
    return {
        message: `No se pudo cotizar.${detailText}`.trim(),
        recovery: error.httpStatus >= 500 ? 'retry' : 'none',
    };
}

/**
 * Fallo de la cotización ya clasificado: mensaje, acción y si se corrige en esta pantalla.
 * `priceCents` convierte a pesos los pct del silo; sin él esos mensajes quedan en su genérico.
 */
export function quoteFailure(error: unknown, priceCents?: number): QuoteFailure {
    const failure = baseQuoteFailure(error, priceCents);
    const code = error instanceof ApiError ? error.code : '';
    return { ...failure, adjustableHere: ADJUSTABLE_HERE.has(code) };
}

/** Fallo sin llamar al silo: la venta llegó a cotizar con una ancla que SureKeep no financia. */
export function productNotFinanceableFailure(): QuoteFailure {
    return { ...FAILURE_BY_CODE.PRODUCT_NOT_FINANCEABLE, adjustableHere: false };
}

/**
 * Mensaje del callejón sin salida: el enganche que haría falta pasa del que el plan permite, así
 * que NINGÚN monto sirve. No pide ajustar nada — lo único que queda es otro equipo u otra forma
 * de pago, y los botones de la pantalla ya las ofrecen.
 */
export function noFeasibleDownPaymentMessage(
    priceCents: number,
    maxFinancedCents: number | undefined,
): string {
    const equipo = formatMXN(priceCents / 100);
    if (maxFinancedCents === undefined) {
        return `El nivel de crédito de este cliente no alcanza para un equipo de ${equipo}.`;
    }
    return `El nivel de crédito de este cliente financia hasta ${formatMXN(maxFinancedCents / 100)} y no alcanza para un equipo de ${equipo}: no hay enganche que lo haga posible.`;
}
